/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the base layout for the search view.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { useCallback } from 'react';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI components.
 */
import { Chip, Avatar, Grid, Container } from '@material-ui/core';

/**
 * Used to display SVG icons.
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

/**
 * SVG icon definition
 */
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';

/**
 * SVG icon definition
 */
import { faList } from '@fortawesome/pro-solid-svg-icons/faList';

/**
 * Hook to get access to the react-router state.
 */
import { useHistory } from 'react-router-dom';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of version data loaded from the ServiceStack API.
 */
import { useVersionSelector, VersionSelectors } from 'src/store/reducers/version';

/**
 * Loading indicator
 */
import Loading from 'src/components/Loading';

/**
 * used to update app state and post messages to opener window.
 */
import useAppState from 'src/hooks/useAppState';

/**
 * Enumeration
 */
import { WindowView } from 'src/utils/MessageManager';

/*
 * ---------------------------------------------------------------------------------
 * interfaces / types
 * ---------------------------------------------------------------------------------
 */

interface IIntroductionProps {
    getScrollElement: () => HTMLElement | null;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    intro: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        margin: 'auto'
    },
    chip: {
        width: '100%',
        justifyContent: 'flex-start',
        transform: 'scale(1.25)',
        transformOrigin: '0 0',

        '&': {
            width: '80%',
        }
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const Introduction: React.FunctionComponent<IIntroductionProps> = ({}) => {
    const classes = useStyles();

    const currentVersion = useVersionSelector(VersionSelectors.selectVersion);

    if (!currentVersion) {
        return (
            <Loading />
        )
    }

    const history = useHistory();

    useAppState(
        WindowView.Introduction,
        null,
        null,
        null,
        null,
        null
    );

    const onSearchClick = useCallback(() => {
        history.push(`/${currentVersion.code}/search`)
    }, [history, currentVersion]);

    const onBrowseClick = useCallback(() => {
        history.push(`/${currentVersion.code}/browse`)
    }, [history, currentVersion]);

    return (
        <Container
            className={classes.intro}
        >
            <Grid
                container
                spacing={2}
            >
                <Grid
                    item
                    xs={6}
                >
                    <Chip
                        className={classes.chip}
                        avatar={(
                            <Avatar>
                                <FontAwesomeIcon fixedWidth icon={faSearch} />
                            </Avatar>
                        )}
                        label="Find a CTCAE Grade"
                        color="primary"
                        onClick={onSearchClick}
                    />
                </Grid>
                <Grid
                    item
                    xs={6}
                >
                    <Chip
                        className={classes.chip}
                        avatar={(
                            <Avatar>
                                <FontAwesomeIcon fixedWidth icon={faList} />
                            </Avatar>
                        )}
                        label="Browse All Categories"
                        color="secondary"
                        onClick={onBrowseClick}
                    />
                </Grid>
            </Grid>
        </Container>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default Introduction;


// WEBPACK FOOTER //
// ./src/containers/Introduction.tsx